import { getContext } from 'svelte';

/**
 * Safely sets inline styles on an element with proper nonce handling
 * @param element The DOM element to style
 * @param property The CSS property name
 * @param value The CSS property value
 */
export function setSecureStyle(element: HTMLElement, property: string, value: string): void {
	try {
		// Try to get nonce from context (if in Svelte component)
		let nonce: string | undefined;
		try {
			nonce = getContext('nonce');
		} catch {
			// Not in Svelte context, continue without nonce
		}

		// Set the style property
		element.style.setProperty(property, value);

		// If we have a nonce, add it to the element for CSP
		if (nonce && element.style.cssText) {
			(element as any).setAttribute('nonce', nonce);
		}
	} catch (error) {
		console.warn('Failed to set secure style:', error);
		// Fallback: set style without nonce
		element.style.setProperty(property, value);
	}
}

/**
 * Safely sets multiple inline styles on an element with proper nonce handling
 * @param element The DOM element to style
 * @param styles Object containing CSS properties and values
 */
export function setSecureStyles(element: HTMLElement, styles: Record<string, string>): void {
	try {
		// Try to get nonce from context (if in Svelte component)
		let nonce: string | undefined;
		try {
			nonce = getContext('nonce');
		} catch {
			// Not in Svelte context, continue without nonce
		}

		// Set all style properties
		Object.entries(styles).forEach(([property, value]) => {
			element.style.setProperty(property, value);
		});

		// If we have a nonce, add it to the element for CSP
		if (nonce && element.style.cssText) {
			(element as any).setAttribute('nonce', nonce);
		}
	} catch (error) {
		console.warn('Failed to set secure styles:', error);
		// Fallback: set styles without nonce
		Object.entries(styles).forEach(([property, value]) => {
			element.style.setProperty(property, value);
		});
	}
}

/**
 * Safely creates a style element with nonce for CSP
 * @param cssText The CSS text to add
 * @returns The created style element
 */
export function createSecureStyleElement(cssText: string): HTMLStyleElement {
	const style = document.createElement('style');

	try {
		// Try to get nonce from context (if in Svelte component)
		let nonce: string | undefined;
		try {
			nonce = getContext('nonce');
		} catch {
			// Not in Svelte context, continue without nonce
		}

		style.textContent = cssText;

		// Add nonce if available
		if (nonce) {
			style.setAttribute('nonce', nonce);
		}
	} catch (error) {
		console.warn('Failed to create secure style element:', error);
		// Fallback: create style without nonce
		style.textContent = cssText;
	}

	return style;
}
